import type { ComponentProps } from 'react';
import Avatar from '@/components/Custom/Avatar';
import Box from '@/components/UI/Box';
import CustomCard from '@/components/UI/Card';
import Text from '@/components/UI/Text';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import { convertElapsedMsToDisplayTime } from '../../EventClock/EventClock.utils';
import { EventScorekeepingPanelBadgeItemFragment } from '../EventScorekeepingPanel/EventScorekeepingPanelBadge/EventScorekeepingPanelBadgeItem';

export const Card = ({
  children,
  variant,
}: ComponentProps<typeof CustomCard>) => {
  return (
    <CustomCard my="sm" mx="md" variant={variant}>
      {children}
    </CustomCard>
  );
};

const Info = ({ children }: ComponentProps<typeof Box>) => {
  return (
    <Box space="md" flex>
      {children}
    </Box>
  );
};

const PrimaryText = ({ children, variant }: ComponentProps<typeof Text>) => {
  return (
    <Text numberOfLines={1} size="xl" bold variant={variant} flexShrink={1}>
      {children}
    </Text>
  );
};

const SecondaryText = ({ children, variant }: ComponentProps<typeof Text>) => {
  return (
    <Text variant={variant} numberOfLines={2} size="lg" flexShrink={1}>
      {children}
    </Text>
  );
};

const TertiaryText = ({ children, variant }: ComponentProps<typeof Text>) => {
  return (
    <Text variant={variant} numberOfLines={3} size="md" italic flexShrink={1}>
      {children}
    </Text>
  );
};

const SegmentText = ({ children, variant }: ComponentProps<typeof Text>) => {
  return (
    <Text variant={variant} size="xl">
      {children}
    </Text>
  );
};

const TimeText = ({ children, variant }: ComponentProps<typeof Text>) => {
  return (
    <Text variant={variant} size="2xl">
      {children}
    </Text>
  );
};

const Content = ({ children }: ComponentProps<typeof Box>) => {
  return (
    <Box direction="horizontal" alignItems="center" p="lg" space="lg">
      {children}
    </Box>
  );
};

export const EventScorekeepingFeedItemFragment = graphql(/* GraphQL */ `
  fragment EventScorekeepingFeedItem on badge_assignee {
    id
    badge_assignee_attributes(order_by: { badge_attribute: { order: asc } }) {
      id
      value
      formatted_value
      badge_attribute {
        id
        attribute_type
        default_value
      }
    }
    badge {
      id
      title
      color
      can_quick_assign
      attribute_score {
        id
        title
        default_numeric_value
      }
    }
    person {
      id
      name
      avatar_id
      color
    }
    team {
      id
      home_primary_color
    }
    person_team {
      person_id
      team_id
      jersey_number
    }
    event_segment {
      id
      title
      duration
      duration_offset
      is_duration_capped
      event_segment_group {
        id
        direction
      }
    }
    elapsed_segment_ms
  }
`, [EventScorekeepingPanelBadgeItemFragment]);

function getAttributeValuesText(
  badgeAssignee: FragmentOf<typeof EventScorekeepingFeedItemFragment>[],
) {
  const assignees = readFragment(
    EventScorekeepingFeedItemFragment,
    badgeAssignee,
  );

  const primaryAssignee = assignees[0];

  if (primaryAssignee) {
    const assigneeAttributes = primaryAssignee.badge_assignee_attributes;

    const formattedValues = [];
    for (const assigneeAttribute of assigneeAttributes) {
      if (
        !assigneeAttribute.badge_attribute.default_value &&
        assigneeAttribute.value !== null
      ) {
        formattedValues.push(assigneeAttribute.formatted_value);
      }
    }

    return formattedValues.join(', ');
  }

  return null;
}

export default function EventScorekeepingFeedItem({
  fragment,
  variant,
}: {
  fragment: FragmentOf<typeof EventScorekeepingFeedItemFragment>[];
  variant?: ComponentProps<typeof CustomCard>['variant'];
}) {
  const item = readFragment(EventScorekeepingFeedItemFragment, fragment[0]);
  const otherAssignments = fragment.slice(1);
  const attributeValuesText = getAttributeValuesText(fragment);

  const direction = (item?.event_segment?.event_segment_group?.direction ??
    'desc') as 'asc' | 'desc';

  const displayTime = convertElapsedMsToDisplayTime(
    item?.elapsed_segment_ms ?? 0,
    direction,
    item?.event_segment?.duration ?? 0,
    item?.event_segment?.duration_offset ?? null,
  );

  return (
    <Content>
      <Info>
        <Box space="md" direction="horizontal" alignItems="center">
          <Avatar
            id={
              !item?.person_team?.jersey_number
                ? item?.person?.avatar_id || ''
                : ''
            }
            size="lg"
            color={item?.team?.home_primary_color || item?.person?.color || ''}
            placeholderText={item?.person_team?.jersey_number || ''}
          />
          <Box flex>
            <PrimaryText variant={variant}>{item?.badge?.title}</PrimaryText>
            <SecondaryText variant={variant}>
              {item?.person?.name}
            </SecondaryText>
            {attributeValuesText ? (
              <TertiaryText variant={variant}>
                {attributeValuesText}
              </TertiaryText>
            ) : null}
          </Box>
        </Box>
        {otherAssignments.length ? (
          <Box space="md" pl="md">
            {otherAssignments.map((otherAssignment) => {
              const data = readFragment(
                EventScorekeepingFeedItemFragment,
                otherAssignment,
              );
              return (
                <Box
                  space="xl"
                  direction="horizontal"
                  alignItems="center"
                  key={data.id}
                >
                  <Avatar
                    id={
                      !data?.person_team?.jersey_number
                        ? data?.person?.avatar_id || ''
                        : ''
                    }
                    size="sm"
                    color={
                      data?.team?.home_primary_color ||
                      data?.person?.color ||
                      ''
                    }
                    placeholderText={data?.person_team?.jersey_number || ''}
                  />
                  <TertiaryText variant={variant} key={data.id}>
                    {data.badge?.title} - {data.person?.name}
                  </TertiaryText>
                </Box>
              );
            })}
          </Box>
        ) : null}
      </Info>
      <Box alignItems="flex-end">
        <SegmentText variant={variant}>
          {item?.event_segment?.title}
        </SegmentText>
        <TimeText variant={variant}>
          {displayTime.minutes}:{displayTime.seconds}
        </TimeText>
      </Box>
    </Content>
  );
}
